import ClockServicesIcon from '../../atoms/Icons/ClockServicesIcon';
import { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import BlueExclamation from '../../../assets/images/blue-exclamation.svg';
import { StaticImport } from 'next/dist/shared/lib/get-img-props';
import QRCode from 'qrcode';
import LocalOfferIcon from '../../atoms/Icons/LocalOffer';
import { IconamoonInvoiceFill } from '../../../assets/icons/iconamoon/invoice-fill';
import { ICouponDiscount } from '../../../interface/product.interfaces';
import BtnConfirm from 'ui/components/atoms/BtnConfirm';
import ArrowForward from '../../atoms/Icons/ArrowForward';
import ArrowRight from '../../atoms/Icons/ArrowRight';
import { Dialog, DialogContent, useDialog } from '../Dialog';
import PortalWrapper from '../../atoms/PortalWrapper';
import CloseIcon from '../../atoms/Icons/CloseIcon';
import CarIconFill from '../../atoms/Icons/CarIconFill';
import CarUsedCarIcon from '../../atoms/Icons/CarUsedCarIcon';

const DiscountTermsModalContent = ({
  data,
  onUseClickFn,
  selectedPromo,
  isLoadingApply = false,
  onClickViewQRCode,
}: {
  data: ICouponDiscount;
  selectedPromo?: ICouponDiscount;
  onUseClickFn?: (promo: ICouponDiscount, ctaButton?: string) => void;
  isLoadingApply?: boolean;
  onClickViewQRCode?: (voucherData: ICouponDiscount) => void;
}) => {
  const PUBLIC_URL = process.env.NEXT_PUBLIC_URL;
  const [qrEncodedImage, setQrEncodedImage] = useState('');
  const seeQRDialog = useDialog();
  const cartTotalLabel = useMemo(() => {
    switch (data?.cartTotal?.[0]?.operator) {
      case 'GREATER_THAN_OR_EQUAL': {
        return 'Minimal Transaksi';
      }
      case 'GREATER_THAN': {
        return 'Transaksi Di Atas';
      }
      case 'LESS_THAN_OR_EQUAL': {
        return 'Maksimal Transaksi';
      }
      case 'LESS_THAN': {
        return 'Transaksi Di Bawah';
      }
      default: {
        return 'Transaksi';
      }
    }
  }, [data?.cartTotal?.[0]?.operator]);

  const formatEndDate = useMemo(() => {
    if (data?.endDate) {
      const date = data?.endDate?.split(' ')[0];
      const splitDate = date?.split('-');
      const correctDate = `${splitDate[1]}/${splitDate[0]}/${splitDate[2]}`;
      return new Intl.DateTimeFormat('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })?.format(new Date(correctDate));
    }
    return null;
  }, [data]);

  const QRDialog = () => {
    return (
      <PortalWrapper>
        <Dialog {...seeQRDialog}>
          <DialogContent
            className="h-fit w-fit bg-transparent justify-center items-center"
            wrapperClassName="z-[999] !bg-black !justify-center"
          >
            <button
              onClick={() => seeQRDialog.setOpen(false)}
              className="absolute top-10 right-10 bg-white rounded-full p-2"
            >
              <CloseIcon fill="#000" />
            </button>
            <Image
              alt="qr-code"
              src={qrEncodedImage}
              width={214}
              height={214}
              className="md:w-[378px] md:h-[378px]"
              quality={100}
            />
          </DialogContent>
        </Dialog>
      </PortalWrapper>
    );
  };

  useEffect(() => {
    if (data?.couponCode) {
      QRCode.toDataURL(data?.couponCode)
        .then((url) => {
          setQrEncodedImage(url);
        })
        .catch((err) => {
          console.error(err);
        });
    }
  }, [data]);

  return (
    <>
      {/* Highlight */}
      <div className="flex flex-col w-full gap-3 px-4 pt-3 pb-4 border-b-2 border-reliableBlack5 md:border-b-4 md:px-6 md:pt-0">
        {data.reward && (
          <div className="flex items-center justify-between gap-8">
            <p className="flex items-center gap-2">
              <LocalOfferIcon fill="#CE181E" className={'h-4 w-4 md:h-[18px] md:w-[18px]'} />
              <span className="inline-block text-sm md:text-base">Promo</span>
            </p>
            <p className="text-sm font-semibold md:text-base break-all line-clamp-2">
              {data.reward}
            </p>
          </div>
        )}

        {data?.endDate && (
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2">
              <ClockServicesIcon className={'h-4 w-4 md:h-[18px] md:w-[18px]'} />
              <span className="inline-block text-sm md:text-base">Berlaku Hingga</span>
            </p>
            <p className="text-sm font-semibold md:text-base">{formatEndDate}</p>
          </div>
        )}

        {data?.cartTotal?.[0]?.formattedValue &&
          Boolean(data?.cartTotal?.[0]?.value) &&
          cartTotalLabel && (
            <div className="flex items-center justify-between">
              <p className="flex items-center gap-2">
                <IconamoonInvoiceFill
                  color="#CE181E"
                  className={'h-4 w-4 md:h-[18px] md:w-[18px]'}
                />
                <span className="inline-block text-sm md:text-base">{cartTotalLabel}</span>
              </p>
              <p className="text-sm font-semibold md:text-base">
                {data?.cartTotal?.[0]?.formattedValue}
              </p>
            </div>
          )}

        {data?.vehicle?.[0]?.modelName && data?.vehicle?.[0]?.policeNo && (
          <div className="flex items-center justify-between">
            <p className="flex items-center gap-2">
              <CarIconFill fill="#CE181E" className={'h-4 w-4 md:h-[18px] md:w-[18px]'} />
              <span className="inline-block text-sm md:text-base">Kendaraan</span>
            </p>
            <p className="text-sm font-semibold md:text-base">{`${data?.vehicle?.[0]?.modelName} - ${data?.vehicle?.[0]?.policeNo}`}</p>
          </div>
        )}
      </div>

      {data?.isLoyaltyGift && qrEncodedImage && !onUseClickFn && (
        <div className="pt-4 px-4" id="qr-code-section">
          <div className="border rounded-[4px] overflow-clip">
            <div className="bg-[#E9F6FF] px-4 py-3 flex gap-2.5 items-center">
              <Image
                className="h-5 w-5"
                src={BlueExclamation as unknown as StaticImport}
                alt="info icon"
              />
              <p className="text-[10px] leading-[12px] font-medium md:text-[14px] md:leading-[17px]">
                Tunjukan QR Code ini saat melakukan transaksi di cabang.
              </p>
            </div>
            <div className="px-2.5 pb-2 flex">
              <button
                onClick={() => {
                  seeQRDialog.setOpen(true);
                  onClickViewQRCode(data);
                }}
                className="flex flex-col items-center cursor-pointer"
                aria-label="Lihat QR Code"
              >
                <Image alt="qr-code" src={qrEncodedImage} width={122} height={122} />
                <div className="bg-reliableBlack text-white flex items-center pl-2 text-[10px] leading-3 font-semibold w-fit rounded-[4px]">
                  Lihat QR Code
                  <ArrowRight fill="#FFF" />
                </div>
              </button>
              <div className="flex-1 flex flex-col gap-1 items-center justify-center text-[12px] leading-[15px]">
                <p>Kode Gift:</p>
                <p className="font-semibold md:text-[14px] md:leading-[17px]">{data.couponCode}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Detail list */}
      <div className="px-4 pt-4 pb-[60px] md:pb-0 md:px-6">
        <p className="text-xs font-semibold text-reliableBlack mb-2 md:text-sm md:mb-4 text-left">
          Syarat & Ketentuan
        </p>
        <div
          className={`prose prose-strong:text-reliableBlack90 prose-ul:text-reliableBlack90 prose-li:leading-[18px] prose-li:text-[12px] md:prose-li:text-[14px] md:prose-li:leading-[18px] ${onUseClickFn ? 'pb-[5rem] md:pb-0' : ''}`}
          dangerouslySetInnerHTML={{
            __html: data?.termsCondition,
          }}
          style={{
            overflowWrap: 'break-word',
          }}
        />
        {onUseClickFn && (
          <div className="fixed w-full left-0 bottom-0 md:static md:bottom-auto md:left-auto">
            <BtnConfirm
              className={`w-full text-white px-8 py-6 lg:p-4 mt-4 ${selectedPromo ? 'bg-reliableBlack20' : 'bg-supportiveRed'}`}
              size="large"
              iconType="fill"
              disabled={!!selectedPromo}
              onClick={() => onUseClickFn(data, 'gunakan promo')}
            >
              <h3 className="uppercase lg:normal-case">
                {isLoadingApply ? 'Loading' : 'Gunakan Promo'}
              </h3>
              <span className="md:hidden">
                <ArrowForward size={20} color="#FFFEFE" />
              </span>
            </BtnConfirm>
          </div>
        )}
      </div>
      {data?.isLoyaltyGift && !onUseClickFn && (
        <a
          className="fixed bottom-0 w-full px-4 py-[17px] flex justify-end items-center bg-supportiveRed text-white uppercase font-bold"
          href={`${PUBLIC_URL}/services-toyota/booking-service?referrer=my-gift`}
          onClick={() => {
            const selectedGift = JSON.stringify(data);
            sessionStorage.setItem('selectedGift', selectedGift);
          }}
        >
          Booking Servis Sekarang
          <ArrowForward size={20} color="#FFFEFE" />
        </a>
      )}
      <QRDialog />
    </>
  );
};

export default DiscountTermsModalContent;
